<template>
  <div v-if="stats" class="revenue-summary">
    <div class="revenue-section-h">
      Main festival payments
      <span class="revenue-section-sub">(deposit + 6 scheduled)</span>
    </div>
    <div class="revenue-row">
      <span class="revenue-label">Total Expected Revenue</span>
      <span class="revenue-value">${{ stats.mainDue.toLocaleString() }}</span>
      <span class="revenue-net">→ ${{ stats.mainDueAfterFees.toLocaleString() }} after ~3% Stripe fees</span>
    </div>
    <div class="revenue-row">
      <span class="revenue-label">Collected So Far</span>
      <span class="revenue-value">${{ stats.mainPaid.toLocaleString() }}</span>
      <span class="revenue-net">→ ${{ stats.mainPaidAfterFees.toLocaleString() }} after ~3% Stripe fees</span>
    </div>
    <div class="revenue-row">
      <span class="revenue-label">Outstanding (not yet paid)</span>
      <span class="revenue-value">${{ stats.mainOutstanding.toLocaleString() }}</span>
    </div>

    <div class="revenue-section-h revenue-section-extras">
      Extras
      <span class="revenue-section-sub">(unpaid costume orders + meals/banquet)</span>
    </div>
    <div class="revenue-row">
      <span class="revenue-label">Expected Extras</span>
      <span class="revenue-value">${{ stats.extrasDue.toLocaleString() }}</span>
      <span class="revenue-net">Will roll into the next unpaid scheduled payment via the lodging editor + similar tools.</span>
    </div>
    <p v-if="loading" class="revenue-loading">Updating dancer totals…</p>
  </div>
</template>

<script>
import { db } from '@/firebase'
import { dancersCollection } from '@/config/iabf-event'
import { iabfForYear } from '@/config/iabf-collections'
import { sumDancerPackages } from '@/utils/iabf-budget-outlook'

export default {
  name: 'FestivalRevenueSummary',
  props: {
    year: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      dancers: [],
      guests: []
    }
  },
  computed: {
    stats () {
      return this.computeStats()
    }
  },
  watch: {
    year: {
      immediate: true,
      handler () {
        this.reload()
      }
    }
  },
  methods: {
    computeStats () {
      const colName = dancersCollection(this.year)
      if (!colName) {
        return null
      }
      let extrasDue = 0
      this.dancers.forEach((row) => {
        extrasDue += this.outstandingFor(row)
      })
      const packages = sumDancerPackages(this.dancers)
      const mainDue = Math.round(packages.dueCents / 100)
      const mainPaid = Math.round(packages.paidCents / 100)
      return {
        mainDue,
        mainPaid,
        mainOutstanding: mainDue - mainPaid,
        extrasDue,
        mainDueAfterFees: Math.round(mainDue * 0.97),
        mainPaidAfterFees: Math.round(mainPaid * 0.97)
      }
    },
    outstandingFor (dancer) {
      const totalCost = Number(dancer.costumeTotalCost) || 0
      const costumeBalance = (dancer.costumeOrderCompleted && !dancer.costumeOrderPaid)
        ? totalCost
        : 0
      const guestBalance = this.guests
        .filter(g => g.dancerId === dancer.id && g.paymentStatus !== 'paid')
        .reduce((sum, g) => sum + (Number(g.totalCost) || 0), 0)
      return costumeBalance + guestBalance
    },
    async reload () {
      const colName = dancersCollection(this.year)
      if (!colName) {
        this.dancers = []
        this.guests = []
        return
      }
      this.loading = true
      try {
        const cols = iabfForYear(this.year)
        const guestCol = cols && cols.guestRegistrations
        const [dancerSnap, guestSnap] = await Promise.all([
          db.collection(colName).get(),
          guestCol ? db.collection(guestCol).get() : Promise.resolve({ docs: [] })
        ])
        this.dancers = dancerSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        this.guests = (guestSnap.docs || []).map((doc) => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.warn('festival revenue summary failed', e)
        this.dancers = []
        this.guests = []
      }
      this.loading = false
    }
  }
}
</script>

<style scoped>
.revenue-summary {
  background: #f5f0eb;
  border-radius: 6px;
  padding: 16px 20px;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.revenue-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  flex-wrap: wrap;
}
.revenue-label {
  color: #888;
  min-width: 220px;
}
.revenue-value {
  font-weight: 700;
  color: #1a1a1a;
  min-width: 100px;
}
.revenue-net {
  color: #5a3e2b;
  font-size: 14px;
}
.revenue-section-h {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a1a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.revenue-section-extras {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #d9d2c5;
}
.revenue-section-sub {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 12px;
  color: #888;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}
.revenue-loading {
  margin: 4px 0 0;
  font-size: 13px;
  color: #888;
}
</style>
